<script setup lang="ts">
/**
 * FormRow — wraps a form field with label + helper + error message.
 *
 * RULES (the deduplication contract):
 *
 *  1. FormRow is the ONLY place that renders the error TEXT.
 *  2. Wrapped fields (Input, Select, Textarea, Checkbox, Switch) may
 *     receive an `error` prop and use it for visual state ONLY
 *     (e.g. red border, aria-invalid="true"). They MUST NOT render
 *     the error message themselves.
 *  3. Helper text and error text are mutually exclusive — when an
 *     error is present, the helper is hidden so the row doesn't
 *     stack two strings.
 *
 * If both `helper` and `error` need to coexist (rare), wrap in a
 * <FormRow> + a sibling helper paragraph in the parent template.
 */
import { computed } from 'vue'

interface Props {
  label?: string
  helper?: string
  error?: string
  required?: boolean
  inline?: boolean
}
const props = defineProps<Props>()

const hasError = computed(
  () => typeof props.error === 'string' && props.error.length > 0,
)
</script>

<template>
  <div
    :class="[
      'form-row',
      { 'form-row--inline': inline, 'form-row--error': hasError },
    ]"
  >
    <label v-if="label" class="form-row__label">
      <span>{{ label }}</span>
      <span
        v-if="required"
        class="form-row__required"
        aria-hidden="true"
      >*</span>
    </label>

    <div class="form-row__field">
      <slot />
    </div>

    <!-- Single error rendering site. Helper is hidden when error present. -->
    <p
      v-if="hasError"
      class="form-row__error"
      role="alert"
    >{{ error }}</p>
    <p
      v-else-if="helper"
      class="form-row__helper"
    >{{ helper }}</p>
  </div>
</template>

<style scoped>
.form-row {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.form-row--inline {
  flex-direction: row;
  align-items: center;
  gap: 1rem;
}
.form-row__label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-2);
  display: inline-flex;
  align-items: baseline;
  gap: 0.125rem;
}
.form-row__required {
  color: var(--color-danger);
}
.form-row__field {
  display: flex;
  flex-direction: column;
}
.form-row__helper {
  font-size: 0.6875rem;
  color: var(--color-text-4);
  margin: 0;
  line-height: 1.4;
}
.form-row__error {
  font-size: 0.6875rem;
  color: var(--color-danger);
  margin: 0;
  line-height: 1.4;
}
</style>
